import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  DataTable,
  GroupHealthInsuranceIndividualsModal,
  AuthorizedButton,
  PageTitle,
  Container,
  GroupHealthInsuranceIndividualsCancelModal,
  SExportButton,
} from '../../../../../components';
import { tableActions } from '../../../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import useHttpService from '../../../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../../../components/DataTable/type';
import { IEnum } from '../../../../../services/type';
import {
  IFile,
  IWithViewPerm,
  IGroupHealthInsuranceIndividualsDataSource,
  IGroupHealthInsuranceIndividualsInitialValues,
} from '../../../../../types';
import { Flex } from 'antd';
import { modalTypes } from '../../../../../assets/enums';
import { downloadFile } from '../../../../../helpers/File';
import {
  cleanupAfterSubmit,
  prepareModalData,
} from '../../../../../utils/functions';
import useFetchParameters from '../../../../../hooks';
import GroupHealthInsuranceIndividualsService from '../../../../../api-clients/GroupHealthInsuranceIndividualsService';
import EnumService from '../../../../../api-clients/EnumService';
import AuthService from '../../../../../api-clients/AuthService';

const Individuals: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IGroupHealthInsuranceIndividualsDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    IGroupHealthInsuranceIndividualsDataSource[]
  >([]);
  const [cancelledItem, setCancelledItem] = useState<
    IGroupHealthInsuranceIndividualsDataSource | undefined
  >(undefined);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [branchChoices, setBranchChoices] = useState<IEnum[]>([]);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);
  const [policyInfos, setPolicyInfos] = useState<
    { id: number; policy_number_and_type: string }[]
  >([]);
  const [dateOfTermination, setDateOfTermination] = useState<string>('');
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);

  const parameters = useFetchParameters({ search, sort, page });
  const groupHealthInsuranceIndividualsService = useMemo(
    () =>
      new GroupHealthInsuranceIndividualsService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const response = await authService.fetchWithViewPerm(
      'grouphealthinsuranceindividuals',
      formik.values.organization,
    );
    if (response && !('statusText' in response)) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchEnums = async () => {
    const statusChoices = await enumService.fetchPoliciesStatusChoices();
    if (statusChoices && Array.isArray(statusChoices)) {
      setStatusChoices(statusChoices);
    }
    const branchChoices = await enumService.fetchBranchChoices();
    if (branchChoices && Array.isArray(branchChoices)) {
      setBranchChoices(branchChoices);
    }

    const policyTypes =
      await enumService.fetchGroupHealthInsurancePolicyTypes();
    if (policyTypes && Array.isArray(policyTypes)) {
      setPolicyTypes(policyTypes);
    }
  };

  const handleFetchData = async () => {
    const response = await groupHealthInsuranceIndividualsService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<
        IGroupHealthInsuranceIndividualsDataSource[]
      >(
        (
          prev: IGroupHealthInsuranceIndividualsDataSource[],
          cur: IGroupHealthInsuranceIndividualsDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));

    const expandedRows =
      await groupHealthInsuranceIndividualsService.fetchExpandedRows(id);
    if (expandedRows && Array.isArray(expandedRows)) {
      setExpandedRows(expandedRows);
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem =
      await groupHealthInsuranceIndividualsService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('upload_files', e.target.files);

  const handleClickCancel = async (id: number) => {
    try {
      await groupHealthInsuranceIndividualsService.cancelPolicy(id);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceIndividualsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await groupHealthInsuranceIndividualsService.deleteItemById(id);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceIndividualsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await groupHealthInsuranceIndividualsService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (
    values: IGroupHealthInsuranceIndividualsInitialValues,
  ) => {
    try {
      await groupHealthInsuranceIndividualsService.submitForm(
        values,
        selectedItem,
      );

      if (selectedItem) {
        await groupHealthInsuranceIndividualsService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceIndividualsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleChangeCancelledItem = (
    item: IGroupHealthInsuranceIndividualsDataSource,
  ) => {
    setCancelledItem(item);
  };

  const handleSubmitCancelModal = async () => {
    try {
      if (cancelledItem && dateOfTermination.length) {
        await groupHealthInsuranceIndividualsService.cancelIndividual(
          cancelledItem.id,
          dateOfTermination,
        );
        setCancelledItem(undefined);
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceIndividualsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleCloseCancelModal = () => {
    setCancelledItem(undefined);
  };

  const handleFetchGetPolicyNumberAndType = async (organization: number) => {
    if (organization) {
      const policyInfos =
        await enumService.fetchPolicyNumberAndType(organization);
      if (policyInfos && Array.isArray(policyInfos)) {
        setPolicyInfos(policyInfos);
      }
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await groupHealthInsuranceIndividualsService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await groupHealthInsuranceIndividualsService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleClickCancel,
    handleChangeCancelledItem,
    handleSetAdditionalFile,
    organizations,
    statusChoices,
    policyTypes,
  );

  // Form Management
  const formik = useFormik<IGroupHealthInsuranceIndividualsInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IGroupHealthInsuranceIndividualsDataSource,
        IGroupHealthInsuranceIndividualsInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  useEffect(() => {
    if (typeof formik.values.organization === 'number') {
      handleFetchGetPolicyNumberAndType(formik.values.organization);
    }
  }, [formik.values.organization]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Grup Sağlık Sigortası - Fertler" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="grouphealthinsuranceindividuals"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Fert Ekleme Talebi</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IGroupHealthInsuranceIndividualsDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <GroupHealthInsuranceIndividualsModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Grup Sağlık Yeni Fert Ekleme Çıkarma Talebi"
        subTitle="Grup Sağlık Sigortası İçin Yeni Fert Ekleme Çıkarma Talebi"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        statusChoices={statusChoices}
        branchChoices={branchChoices}
        policyInfos={policyInfos}
        withViewPerm={withViewPerm}
      />
      <GroupHealthInsuranceIndividualsModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Grup Sağlık Fert Güncelleme"
        subTitle="Grup Sağlık Sigortası İçin Fert Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        statusChoices={statusChoices}
        branchChoices={branchChoices}
        policyInfos={policyInfos}
        withViewPerm={withViewPerm}
      />
      <GroupHealthInsuranceIndividualsCancelModal
        open={!!cancelledItem}
        title="Sigortalı İptal Talebi"
        subTitle="Grup Sağlık Sigortası İçin Sigortalı İptal Talebi Oluşturma"
        item={cancelledItem}
        organizations={organizations}
        setDateOfTermination={setDateOfTermination}
        handleSubmit={handleSubmitCancelModal}
        handleCancel={handleCloseCancelModal}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="grouphealthinsuranceindividualsadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default Individuals;
